import { Badge, User } from 'common/interfaces/api';
import { SITE_BASE_URL } from 'config/env';
import { AuthError } from 'firebase/auth';
import dayjs from 'lib/dayjs';
import { WebPricePackage, WebPricePackageRecurring } from 'service/payment';

import { stringifyJSON } from '.';
import {
  IG_20000_COIN_EVENT,
  MONEX_20000_COIN_EVENT,
  MOOMOO_10000_COIN_EVENT,
  PRIME_DISCOUNT_202505,
  SAXO_10000_COIN_EVENT,
  TAKA_TRADE_TRADING_EVENT,
  YEAR_START_2025_EVENT,
} from './index.constant';
import {
  addEvent,
  EventAction,
  EventCategory,
  EventComponent,
  ScreenName,
} from './pp_tracking';

export const isAbleToUpgradePlan = (creator: User) => {
  return (
    creator.is_enabled_long_subscription_setting &&
    creator.is_long_subscription_available &&
    creator.creator_subscription &&
    creator.creator_subscription.current_plan &&
    ['active', 'trialing'].includes(creator.creator_subscription.status) &&
    packageLevel(creator.creator_subscription.current_plan) <
      packageLevel(WebPricePackageRecurring.Year) &&
    !['android_iap', 'ios_iap'].includes(creator.creator_subscription?.platform)
  );
};

const packageLevel = (packageName: WebPricePackageRecurring) => {
  switch (packageName) {
    case WebPricePackageRecurring.Month:
      return 1;
    case WebPricePackageRecurring.HalfYear:
      return 2;
    case WebPricePackageRecurring.Year:
      return 3;
    default:
      return 0;
  }
};

export const isChoosingPayAblePackage = (
  currentSubbingPackageName: WebPricePackageRecurring,
  packageName: WebPricePackageRecurring,
) => {
  return packageLevel(currentSubbingPackageName) < packageLevel(packageName);
};

export const showingPackages = (
  subPack: WebPricePackageRecurring | null,
  packages: WebPricePackage[],
  currentPlatform: string,
) => {
  const isSubbingFromApp = ['android_iap', 'ios_iap'].includes(currentPlatform);
  const subPackLevel = packageLevel(subPack);
  const filterCondition = (p: WebPricePackage) => {
    if (isSubbingFromApp && subPackLevel > 0) {
      return packageLevel(p.recurring) === subPackLevel;
    }
    return packageLevel(p.recurring) >= subPackLevel;
  };
  return packages
    .filter((p) => filterCondition(p))
    .sort((a, b) => packageLevel(a.recurring) - packageLevel(b.recurring));
};

export const showDateTo = (date: string, dateFormat: string) => {
  if (!date) return '現在';
  return dayjs(date).format(dateFormat);
};

export const formatDatoToSend = (date?: Date) => {
  if (!date) return dayjs().format('YYYY-MM-DD');
  return dayjs(date).format('YYYY-MM-DD');
};

export const openLoginDialogEvent = (path: string) => {
  addEvent({
    action: EventAction.PageView,
    category: EventCategory.Account,
    screen: ScreenName.HomeTimeline,
    component: EventComponent.LoginDialog,
    timestamp: new Date().toISOString(),
    tracking_params: [
      {
        label: 'path',
        value: path,
      },
    ],
  });
};

export const createAccountEvent = (path: string) => {
  addEvent({
    action: EventAction.Click,
    category: EventCategory.Account,
    screen: ScreenName.HomeTimeline,
    component: EventComponent.LoginDialog,
    timestamp: new Date().toISOString(),
    tracking_params: [
      {
        label: 'path',
        value: path,
      },
    ],
  });
};

export const clickPolicyPopup = (position: 'agree_btn' | 'disagree_btn') => {
  addEvent({
    action: EventAction.Click,
    category: EventCategory.Agent,
    screen: ScreenName.RecruitmentProfile,
    component: EventComponent.AgentFunctionPolicyPopup,
    timestamp: new Date().toISOString(),
    tracking_params: [
      {
        label: 'click_position',
        value: position,
      },
    ],
  });
};

export const viewPolicyPopup = () => {
  addEvent({
    action: EventAction.PageView,
    category: EventCategory.Agent,
    screen: ScreenName.RecruitmentProfile,
    component: EventComponent.AgentFunctionPolicyPopup,
    timestamp: new Date().toISOString(),
    tracking_params: [
      {
        label: 'type',
        value: 'policy_popup',
      },
    ],
  });
};

export const providerInfo = (provider: string) => {
  switch (provider) {
    case 'password':
      return { name: 'email', width: 24, height: 24, label: 'メールアドレス' };
    case 'google.com':
      return { name: 'google', width: 36, height: 36, label: 'Google' };
    case 'facebook.com':
      return {
        name: 'facebook-blue',
        width: 14,
        height: 26,
        label: 'Facebook',
      };
    case 'twitter.com':
      return { name: 'twitter-blue', width: 21, height: 17, label: 'Twitter' };
    case 'apple.com':
      return { name: 'apple-black', width: 20, height: 24, label: 'Apple' };
    default:
      return null;
  }
};

export const errorCodeToText = (error: AuthError) => {
  switch (error.code) {
    case 'auth/credential-already-in-use':
      return 'このアカウントはすでに使用されています。';
    case 'auth/account-exists-with-different-credential':
      return '既に他のソーシャルアカウントで該当メールアドレスを利用してログインしました。';
    default:
      return 'エラーが発生しました。';
  }
};

export const addChangeProviderEvent = ({
  action,
  value,
  label,
}: {
  action: EventAction;
  value: string;
  label: 'current_provider' | 'linked_provider';
}) => {
  addEvent({
    action: action,
    category: EventCategory.Account,
    screen: ScreenName.AccountSetting,
    component: EventComponent.ChangeProvider,
    timestamp: new Date().toISOString(),
    tracking_params: [
      {
        value: value,
        label: label,
      },
    ],
  });
};

export const isLevel5OrAbove = (badge: Badge) => {
  if (!badge) return false;
  const lv = (badge as string).replace('level', '');
  return lv && parseInt(lv) >= 5;
};

export const isLevel4OrAbove = (badge: Badge) => {
  if (!badge) return false;
  const lv = (badge as string).replace('level', '');
  return lv && parseInt(lv) >= 4;
};

export const addPageViewEvent = (path: string) => {
  addEvent({
    action: EventAction.PageView,
    category: EventCategory.WebAnalytics,
    screen: ScreenName.HomeTimeline,
    timestamp: new Date().toISOString(),
    tracking_params: [
      {
        value: path,
        label: 'view_path',
      },
    ],
  });
};

export const isEnableDiscountCampaign = ({
  currentUser,
  creator,
}: {
  currentUser: User;
  creator: User;
}) => {
  if (!creator) return false;
  if (!creator?.is_open_for_subscription || !creator.is_creator) return false;
  const creatorEnableCampaign = creator?.joined_promotions?.some(
    (promotion) => promotion.type === PRIME_DISCOUNT_202505,
  );
  if (!currentUser) return creatorEnableCampaign;
  const discountPrimeSubEvent = getPrimeDiscountEvent(currentUser);
  if (!discountPrimeSubEvent) return false;
  return creatorEnableCampaign;
};

export const onboardingStepName = (step: number) => {
  switch (step) {
    case 0:
      return 'Onboarding Submit Invest Level';
    case 1:
      return 'Onboarding Submit Interested Topic';
    case 2:
      return 'Onboarding View Recommended Creators';
    case 3:
      return 'Onboarding View Paid Service Explanation ';
    case 4:
      return 'Onboarding View PostPrime Video Page';
    default:
      return '';
  }
};

export const getPrimeDiscountEvent = (user: User) => {
  if (!user) return null;
  return user?.applicable_promotions?.find(
    (event) => event.type === PRIME_DISCOUNT_202505,
  );
};

export const getIGEvent = (user: User) => {
  if (!user) return null;
  return user?.applicable_promotions?.find(
    (event) => event.type === IG_20000_COIN_EVENT,
  );
};

export const getMooMooEvent = (user: User) => {
  if (!user) return null;
  return user?.applicable_promotions?.find(
    (event) => event.type === MOOMOO_10000_COIN_EVENT,
  );
};

export const getSaxoEvent = (user: User) => {
  if (!user) return null;
  return user?.applicable_promotions?.find(
    (event) => event.type === SAXO_10000_COIN_EVENT,
  );
};

export const getMonexEvent = (user: User) => {
  if (!user) return null;
  return user?.applicable_promotions?.find(
    (event) => event.type === MONEX_20000_COIN_EVENT,
  );
};

export const getTakaTradeTradingEvent = (user: User) => {
  if (!user) return null;
  return user?.applicable_promotions?.find(
    (event) => event.type === TAKA_TRADE_TRADING_EVENT,
  );
};

export const getUserYearStartEvent = (user: User) => {
  if (!user) return null;
  return user?.applicable_promotions?.find(
    (event) => event.type === YEAR_START_2025_EVENT,
  );
};

/**
 * Generate structured data JSON-LD string for user profile
 * @param userProfile - User profile data
 * @param username - Username from URL
 * @returns JSON-LD structured data as string or empty object string
 */
export const generateUserStructuredData = (userProfile: User): string => {
  if (!userProfile) return stringifyJSON({}) as string;

  try {
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: userProfile.name || userProfile.username || 'PostPrime User',
      ...(userProfile.username && {
        url: `${SITE_BASE_URL}/${userProfile.username}`,
      }),
      ...(userProfile.avatar_url && {
        image: userProfile.avatar_url,
      }),
      ...(userProfile.bio && { description: userProfile.bio }),
      ...(userProfile.job && { jobTitle: userProfile.job }),
      ...(userProfile.username && { identifier: userProfile.username }),
      ...(userProfile.username && {
        sameAs: [`${SITE_BASE_URL}/${userProfile.username}`],
      }),
      ...(userProfile.username && {
        mainEntityOfPage: {
          '@type': 'ProfilePage',
          '@id': `${SITE_BASE_URL}/${userProfile.username}`,
        },
      }),
      knowsAbout:
        userProfile.domains?.map((domain) => domain?.name || '') || [],
      affiliation: {
        '@type': 'Organization',
        name: 'PostPrime',
        url: SITE_BASE_URL,
      },
      ...(userProfile.created_at && {
        memberOf: {
          '@type': 'Organization',
          name: 'PostPrime',
          url: SITE_BASE_URL,
          member: {
            '@type': 'Person',
            name: userProfile.name || userProfile.username || '',
            ...(userProfile.created_at && {
              startDate: userProfile.created_at,
            }),
          },
        },
      }),
      ...(userProfile.is_creator && {
        hasOccupation: {
          '@type': 'Occupation',
          name: 'Content Creator',
          occupationLocation: {
            '@type': 'VirtualLocation',
            name: 'PostPrime Platform',
          },
        },
      }),
    };

    return stringifyJSON(structuredData) as string;
  } catch (error) {
    console.error('Error generating user structured data:', error);
    return stringifyJSON({}) as string;
  }
};
